import React, { useState } from 'react';
import { Plus, Search, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useSubscriptions,
  useDeleteSubscription,
  useSendSubscriptionNow,
  usePauseSubscription,
  useResumeSubscription,
} from '@/hooks/reporting/useSubscriptions';
import SubscriptionCard from '@/components/reports/cards/SubscriptionCard';
import ConfirmDeleteModal from '@/components/reports/modals/ConfirmDeleteModal';
import EmptyState from '@/components/reports/common/EmptyState';
import type { ListSubscriptionsQuery } from '@/types/reporting-v2';

export default function SubscriptionsList() {
  const navigate = useNavigate();
  const deleteSubscription = useDeleteSubscription();
  const sendSubscriptionNow = useSendSubscriptionNow();
  const pauseSubscription = usePauseSubscription();
  const resumeSubscription = useResumeSubscription();

  const [query, setQuery] = useState<ListSubscriptionsQuery>({
    page: 1,
    limit: 12,
  });

  const { data, isLoading, refetch } = useSubscriptions(query);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    subscriptionId?: string;
    subscriptionName?: string;
  }>({
    isOpen: false,
  });

  const handleSearch = () => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined,
    }));
  };

  const handleStatusFilter = (status: 'all' | 'active' | 'paused') => {
    setFilterStatus(status);
    setQuery((prev) => ({
      ...prev,
      page: 1,
      is_active: status === 'all' ? undefined : status === 'active',
    }));
  };

  const handlePageChange = (page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handleSendNow = async (subscriptionId: string) => {
    await sendSubscriptionNow.mutateAsync({ subscriptionId });
    refetch();
  };

  const handlePauseResume = async (subscriptionId: string, isActive: boolean) => {
    if (isActive) {
      await pauseSubscription.mutateAsync(subscriptionId);
    } else {
      await resumeSubscription.mutateAsync(subscriptionId);
    }
    refetch();
  };

  const handleEdit = (subscriptionId: string) => {
    navigate(`/admin/reporting/subscriptions/${subscriptionId}/edit`);
  };

  const handleView = (subscriptionId: string) => {
    navigate(`/admin/reporting/subscriptions/${subscriptionId}`);
  };

  const handleDeleteClick = (subscriptionId: string, subscriptionName: string) => {
    setDeleteModal({
      isOpen: true,
      subscriptionId,
      subscriptionName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.subscriptionId) {
      await deleteSubscription.mutateAsync(deleteModal.subscriptionId);
      setDeleteModal({ isOpen: false });
      refetch();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage automated report delivery via email
          </p>
        </div>
        <button
          onClick={() => navigate('/reports/subscriptions/create')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Subscription
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search subscriptions by name..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Search
            </button>
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleStatusFilter('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => handleStatusFilter('paused')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'paused'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paused
            </button>
          </div>
        </div>
      </div>

      {/* Subscriptions Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : data?.subscriptions && data.subscriptions.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.subscriptions.map((subscription) => (
              <SubscriptionCard
                key={subscription.subscription_id}
                subscription={subscription}
                onSendNow={() => handleSendNow(subscription.subscription_id)}
                onPauseResume={() =>
                  handlePauseResume(subscription.subscription_id, subscription.is_active)
                }
                onEdit={() => handleEdit(subscription.subscription_id)}
                onDelete={() =>
                  handleDeleteClick(subscription.subscription_id, subscription.subscription_name)
                }
                onView={() => handleView(subscription.subscription_id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {data.pagination && data.pagination.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {(data.pagination.page - 1) * data.pagination.limit + 1} to{' '}
                {Math.min(
                  data.pagination.page * data.pagination.limit,
                  data.pagination.total
                )}{' '}
                of {data.pagination.total} subscriptions
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(query.page! - 1)}
                  disabled={query.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(query.page! + 1)}
                  disabled={query.page === data.pagination.total_pages}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <EmptyState
            icon={Plus}
            title="No subscriptions found"
            description="Get started by creating your first email subscription"
            action={{
              label: 'Create Subscription',
              onClick: () => navigate('/reports/subscriptions/create'),
            }}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Subscription"
        message="Are you sure you want to delete this subscription? This action cannot be undone."
        itemName={deleteModal.subscriptionName}
        loading={deleteSubscription.isPending}
      />
    </div>
  );
}
